/**
 * Vẽ ranh giới Vườn trên bản đồ (Doc/09 lát 1b, quyết định #22) — `/plots/:id/boundary`.
 *
 * Chạm lên bản đồ để thêm đỉnh theo thứ tự quanh lô; kéo một đỉnh để dời; "Hoàn tác" bỏ thao
 * tác gần nhất. Hình học (kiểm hình, diện tích) là của @farmer/audit-rules — API kiểm lại đúng
 * bằng hàm đó khi lưu (`PUT /plots/:id/boundary`), nên màn này chỉ báo trước, không quyết định.
 *
 * Leaflet nạp động khi mở màn này: các màn khác không phải tải thư viện bản đồ.
 * Ảnh nền: Mapbox vệ tinh nếu có `VITE_MAPBOX_TOKEN` lúc build, không thì OpenStreetMap.
 */
import * as api from '../api-client.js';
import { boundaryVertices } from '@farmer/audit-rules';
import { buildHash, esc, navigate, toast } from '../ui/util.js';
import {
  AREA_DIFF_WARN_PCT,
  basemapFor,
  initialView,
  summarize,
  summaryText,
} from '../ui/boundary-editor.js';
import { watchMapboxTiles } from '../ui/plot-map.js';

export async function renderPlotBoundary(container, { id }) {
  container.innerHTML = `<div class="empty-state">Đang tải vườn...</div>`;
  let plot;
  try {
    plot = await api.plots.get(id);
  } catch (err) {
    container.innerHTML = `<div class="empty-state">${esc(err.message)}</div>`;
    return undefined;
  }

  const saved = boundaryVertices(plot.boundaryGeojson);
  let vertices = saved.map((v) => [v[0], v[1]]);
  const history = [];
  const basemap = basemapFor(import.meta.env.VITE_MAPBOX_TOKEN);
  const backHash = buildHash(`/households/${plot.householdId}`);

  container.innerHTML = `
    <div class="page-head">
      <div>
        <h1>Ranh giới vườn ${esc(plot.plotCode || '')}</h1>
        <p>Hộ: <b>${esc(plot.household?.fullName || '')}</b> · Diện tích khai báo ${esc(plot.areaHa)} ha.
           Chạm lên bản đồ theo thứ tự quanh lô để đặt đỉnh; kéo đỉnh để dời.</p>
      </div>
      <div class="page-head-actions">
        <a class="btn btn-text" href="${backHash}"><span class="ms">arrow_back</span>Hồ sơ hộ</a>
      </div>
    </div>
    ${basemap.warning ? `<div class="status-banner tone-warn"><span class="ms">warning</span><span>${esc(basemap.warning)}</span></div>` : ''}
    <div class="status-banner tone-error" id="basemapError" role="alert" hidden></div>
    <div class="card boundary-card">
      <div class="boundary-map" id="boundaryMap" role="application" aria-label="Bản đồ vẽ ranh giới vườn"></div>
      <div class="boundary-bar">
        <span class="boundary-summary" id="boundarySummary" aria-live="polite"></span>
        <span class="boundary-problem error-text" id="boundaryProblem" hidden></span>
      </div>
      <div class="form-actions boundary-actions">
        <button class="btn btn-text" type="button" id="btnLocate"><span class="ms">my_location</span>Vị trí của tôi</button>
        <button class="btn btn-text" type="button" id="btnUndo"><span class="ms">undo</span>Hoàn tác</button>
        <button class="btn btn-text" type="button" id="btnClear"><span class="ms">layers_clear</span>Vẽ lại từ đầu</button>
        ${saved.length ? '<button class="btn btn-text danger" type="button" id="btnDelete"><span class="ms">delete</span>Xoá ranh giới đã lưu</button>' : ''}
        <button class="btn" type="button" id="btnSave"><span class="ms">save</span>Lưu ranh giới</button>
      </div>
    </div>`;

  const [{ default: L }] = await Promise.all([import('leaflet'), import('leaflet/dist/leaflet.css')]);

  const map = L.map(container.querySelector('#boundaryMap'), { zoomControl: true });
  const baseLayer = L.tileLayer(basemap.url, basemap.options).addTo(map);
  if (basemap.kind === 'mapbox') {
    watchMapboxTiles(map, baseLayer, L, container.querySelector('#basemapError'), import.meta.env.VITE_MAPBOX_TOKEN);
  }
  // Leaflet dùng [vĩ độ, kinh độ]; GeoJSON (và `vertices`) dùng [kinh độ, vĩ độ].
  const toLatLng = ([lng, lat]) => [lat, lng];

  const view = initialView(vertices, plot);
  if (view.fitVertices) map.fitBounds(L.latLngBounds(vertices.map(toLatLng)), { padding: [32, 32], maxZoom: 19 });
  else map.setView([view.lat, view.lng], view.zoom);

  const shape = L.polygon([], { color: '#f5c542', weight: 3, fillOpacity: 0.15 }).addTo(map);
  const markers = L.layerGroup().addTo(map);
  const vertexIcon = (n) =>
    L.divIcon({ className: 'boundary-vertex', html: `<span>${n}</span>`, iconSize: [26, 26], iconAnchor: [13, 13] });

  const summaryEl = container.querySelector('#boundarySummary');
  const problemEl = container.querySelector('#boundaryProblem');
  const saveBtn = container.querySelector('#btnSave');
  const undoBtn = container.querySelector('#btnUndo');

  function remember() {
    history.push(vertices.map((v) => [v[0], v[1]]));
  }

  function redraw() {
    shape.setLatLngs(vertices.map(toLatLng));
    markers.clearLayers();
    vertices.forEach((v, i) => {
      const m = L.marker(toLatLng(v), { icon: vertexIcon(i + 1), draggable: true, keyboard: false });
      m.on('dragstart', remember);
      m.on('drag', (e) => {
        const p = e.target.getLatLng();
        vertices[i] = [p.lng, p.lat];
        shape.setLatLngs(vertices.map(toLatLng));
      });
      m.on('dragend', update);
      m.addTo(markers);
    });
    update();
  }

  function update() {
    const s = summarize(vertices, plot.areaHa);
    summaryEl.textContent = summaryText(s, plot.areaHa);
    const warnArea = s.canSave && s.diffPct != null && Math.abs(s.diffPct) > AREA_DIFF_WARN_PCT;
    problemEl.hidden = !s.problem && !warnArea;
    problemEl.textContent = s.problem || (warnArea ? `Chênh hơn ${AREA_DIFF_WARN_PCT}% so với diện tích khai báo — kiểm lại lô hoặc diện tích.` : '');
    problemEl.classList.toggle('is-warning', !s.problem && warnArea);
    saveBtn.disabled = !s.canSave;
    undoBtn.disabled = history.length === 0;
  }

  map.on('click', (e) => {
    remember();
    vertices.push([e.latlng.lng, e.latlng.lat]);
    redraw();
  });

  undoBtn.addEventListener('click', () => {
    if (!history.length) return;
    vertices = history.pop();
    redraw();
  });

  container.querySelector('#btnClear').addEventListener('click', () => {
    if (!vertices.length) return;
    remember();
    vertices = [];
    redraw();
  });

  container.querySelector('#btnLocate').addEventListener('click', () => {
    if (!navigator.geolocation) {
      toast('Thiết bị không hỗ trợ định vị');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => map.setView([pos.coords.latitude, pos.coords.longitude], Math.max(map.getZoom(), 18)),
      (err) => toast(`Không lấy được vị trí: ${err.message}`),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });

  saveBtn.addEventListener('click', async () => {
    const s = summarize(vertices, plot.areaHa);
    if (!s.canSave) return;
    saveBtn.disabled = true;
    try {
      await api.plots.setBoundary(plot.id, s.polygon);
      toast('Đã lưu ranh giới vườn');
      navigate(backHash);
    } catch (err) {
      toast(`Không lưu được: ${err.message}`);
      update();
    }
  });

  container.querySelector('#btnDelete')?.addEventListener('click', async () => {
    if (!window.confirm('Xoá ranh giới đã lưu của vườn này?')) return;
    try {
      await api.plots.setBoundary(plot.id, null);
      toast('Đã xoá ranh giới vườn');
      navigate(backHash);
    } catch (err) {
      toast(`Không xoá được: ${err.message}`);
    }
  });

  redraw();
  // Bản đồ đặt trong khung vừa dựng — đo lại kích thước sau khi trình duyệt dàn trang xong.
  setTimeout(() => map.invalidateSize(), 0);
  return () => map.remove();
}
